/* eslint-disable @typescript-eslint/max-params */
'use client';

import { getSportradarIdToken } from '../../providers/sportradar/entry.js';

type SirWidgetConfig = {
  language: string;
  clockType: '12' | '24';
  oddsType: 'eu' | 'uk' | 'us';
  theme: string | false;
  debug: boolean;
};

type WidgetReference = {
  key: string;
  el: HTMLElement | string;
};

const rootWidgetClass = 'sr-widget';

/**
 * language {string}: any of our supported language codes
 * clockType {string}: '12' or '24'. Clock type to use when displaying time.
 * oddsType {string}: 'eu', 'uk' or 'us'. Odds type to use across all widgets: UK (fractional), EU (decimal) or US (american).
 * adserverClientId {int}: Id from sportradar's adserver.
 * matchId {string|number}: default match for all widgets to initialize.
 * Use this global matchId if you want widgets to automatically change match when it is selected by some other widget (ie. match list)
 * teamId {string|number}: default team id for all widgets
 * theme {string|false}: which theme will be used. If no theme is specified, it will download the theme created in our client setup process.
 * If set to false no theme will be applied. Beside this, we also have few public themes.
 * These are 'betradar', 'sportradar', 'betradardark' and 'sportradardark'. If you wish to use your own theme you will need to provide one. You can create it on our sportradar widget store.
 * useTeamColors {boolean}: whether to explicitly enable or disable the usage of team-specific colors. By default it is set to "true".
 * If team-specific colors are enabled, then teamColorsTheme must also be provided in order to actually use team colors. See teamColorsTheme for details.
 * teamColorsTheme {string}: which theme is being used in combination with team colors, where colors are associated to a particular team.
 * This is meant to be used with widget store, which generates the appropriate value for teamColorsTheme. Example team formula: 'mono-team-colors-light'.
 * NOTE: This works only for specific widgets and sports and / or tournaments. Example: NFL.
 * logLevel {'error'|'warn'|'info'}: Sets initial log level. By default, nothing is logged.
 * debug {any}: if set to anything but false, 'logLevel' will change to warn. Plays nice with html5, since only specifying attribute is enough.
 */
const sirWidgetConfig: SirWidgetConfig = {
  language: 'se',
  clockType: '24',
  oddsType: 'eu',
  theme: false,
  debug: true,
};

const tick = 3000;
const interval: Record<string, ReturnType<typeof setInterval> | undefined> = {};
const widgetReferences: WidgetReference[] = [];

function getSirWidgetConfig(): SirWidgetConfig {
  return sirWidgetConfig;
}

/**
 * Starts the Sportradar SIR script if it is not already loaded.
 *
 * @returns A promise that resolves when the SIR script is loaded or rejects if there is an error
 */
export function startSirScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    let loadingTimeout: ReturnType<typeof setTimeout> | undefined;

    const clearLoadingTimer = (): void => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };

    const sirOnError = (error: Error): void => {
      clearLoadingTimer();
      reject(error);
    };

    const sirOnLoad = (): void => {
      clearLoadingTimer();
      resolve();
    };

    try {
      if (window.SIR) {
        resolve();
        return;
      }

      window.SIR = (...args: unknown[]): void => {
        const sir = window.SIR;
        if (!sir) {
          return;
        }
        (sir.q = sir.q || []).push(args);
      };

      window.SIR.l = Date.now();
      window.SIR.o = getSirWidgetConfig();

      const windowSirScript = document.createElement('script');
      const [firstScript] = document.querySelectorAll('script');

      windowSirScript.async = true;
      windowSirScript.addEventListener('load', () => {
        sirOnLoad();
      });
      windowSirScript.addEventListener('error', (e: Event) => {
        const currentError =
          e instanceof ErrorEvent ? new Error(e.message) : new Error('Unknown error loading SIR script');
        sirOnError(currentError);
      });

      const SPORTRADAR_LICENSE_ID = getSportradarIdToken();

      windowSirScript.src = `https://widgets.sir.sportradar.com/${SPORTRADAR_LICENSE_ID}/widgetloader`;
      windowSirScript.setAttribute('n', 'SIR');

      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(windowSirScript, firstScript);
      } else {
        document.head.append(windowSirScript);
      }

      loadingTimeout = setTimeout(() => {
        sirOnError(new Error('Failed to load sportradar resources'));
        // eslint-disable-next-line unicorn/numeric-separators-style
      }, 30000);
    } catch (error: unknown) {
      const currentError =
        error instanceof ErrorEvent ? new Error(error.message) : new Error('Unknown error starting SIR script');
      sirOnError(currentError);
    }
  });
}

/**
 * Generates a unique key for a widget based on match ID and widget type.
 *
 * @param matchId - The ID of current match
 * @param widgetType - The type of the widget to create
 * @returns A unique key string for the widget
 */
export function getKeyByMatchIdAndType(matchId: string | number, widgetType: string): string {
  return `${matchId}-${widgetType.replaceAll(/\./gi, '')}`;
}

/**
 * Generates the CSS class name for a widget based on match ID and widget type.
 *
 * @param matchId - The ID of current match
 * @param widgetType - The type of the widget to create
 * @param shouldUseDot - Whether to prefix the class name with a dot (default: true)
 * @returns The CSS class name for the widget
 */
export function getWidgetClassByMatchId(
  matchId: string | number,
  widgetType: string,
  shouldUseDot: boolean = true
): string {
  return `${shouldUseDot ? '.' : ''}${rootWidgetClass}-${getKeyByMatchIdAndType(matchId, widgetType)}`;
}

/**
 * Updates a Sportradar widget with new properties.
 *
 * @param matchId - The ID of current match
 * @param widgetType - The type of the widget to update
 * @param widgetProps - Additional properties for the widget
 * @param callback - Optional callback function to be called after the widget is updated
 */
export function updateWidget(
  matchId: string | number,
  widgetType: string,
  widgetProps: Record<string, unknown> = {},
  callback?: (...args: unknown[]) => void
): void {
  const props = { ...widgetProps, matchId };
  window.SIR?.('addWidget', getWidgetClassByMatchId(matchId, widgetType), widgetType, props, callback);
}

/**
 * Removes all Sportradar widgets associated with the specified match ID and widget type.
 *
 * @param matchId - The ID of current match
 * @param widgetType - The type of the widget to remove
 */
export function removeWidgets(matchId: string | number, widgetType: string): void {
  for (const { key, el } of widgetReferences) {
    if (key === getKeyByMatchIdAndType(matchId, widgetType)) {
      window.SIR?.('removeWidget', el);
    }
  }
}

/**
 * Clears the update timer for a specific Sportradar widget.
 *
 * @param matchId - The ID of current match
 * @param widgetType - The type of the widget to clear the timer for
 */
export function clearTimer(matchId: string | number, widgetType: string): void {
  const key = getKeyByMatchIdAndType(matchId, widgetType);
  const timer = interval[key];
  if (timer) {
    clearInterval(timer);
    interval[key] = undefined;
  }
}

/**
 * Sets up a reference for a Sportradar widget.
 *
 * @param matchId - The ID of current match
 * @param widgetType - The type of the widget to set up the reference for
 * @param el - The HTML element or selector string for the widget
 */
export function setupWidgetReference(matchId: string | number, widgetType: string, el: HTMLElement | string): void {
  widgetReferences.push({
    key: getKeyByMatchIdAndType(matchId, widgetType),
    el,
  });
}

/**
 * Starts the update timer for a specific Sportradar widget.
 *
 * @param matchId - The ID of current match
 * @param widgetType - The type of the widget to start the timer for
 * @param widgetProps - Additional properties for the widget
 */
export function startTimer(matchId: string | number, widgetType: string, widgetProps?: Record<string, unknown>): void {
  const key = getKeyByMatchIdAndType(matchId, widgetType);
  if (interval[key]) {
    clearTimer(matchId, widgetType);
  }
  interval[key] = setInterval(() => {
    updateWidget(matchId, widgetType, widgetProps);
  }, tick);
}
